/**
 * ARTICLE D'EXEMPLE — généré le 2026-09-06 pour valider la maquette du blog.
 * À relire, compléter ou remplacer par du contenu réel avant/après mise en ligne.
 * Sources : 6 URL vérifiées automatiquement (6 OK), article révisé après vérification.
 * Format : voir src/blog/README.md
 */
export default {
  "slug": "besancon-numerique-demarches-open-data-fibre-inclusion",
  "title": "Besançon et le numérique : démarches en ligne, open data, fibre et inclusion",
  "excerpt": "Démarches en ligne, open data, fibre, inclusion : tour d’horizon de ce que la Ville de Besançon et Grand Besançon Métropole font pour le numérique, et où trouver l’information officielle.",
  "seoTitle": "Grand Besançon Métropole et le numérique",
  "seoDescription": "Portail Mes démarches, une centaine de jeux de données ouverts, déploiement de la fibre et conseillers numériques : ce que propose Grand Besançon Métropole.",
  "category": "collectivites",
  "tags": [
    "Grand Besançon Métropole",
    "Open Data",
    "Fibre optique",
    "Inclusion numérique",
    "Services publics"
  ],
  "author": "jimmy",
  "publishedAt": "2026-07-31",
  "updatedAt": "2026-07-31",
  "featured": false,
  "cover": {
    "image": "/img/blog/besancon-la-vieille-ville-vue-depuis-les-glacis.jpg",
    "credit": "Photo : Besançon, la vieille ville vue depuis les Glacis — Wikipedro, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Façade de l’hôtel de ville de Besançon, avec au premier plan une personne consultant une démarche administrative sur son smartphone"
  },
  "content": [
    {
      "type": "lead",
      "text": "Payer une facture, signaler un lampadaire en panne, récupérer les horaires du tram ou vérifier si la fibre arrive chez soi : à Besançon, une bonne partie de la relation entre les habitants et leur collectivité passe désormais par un écran. Cet article fait le point sur ce que la Ville de Besançon et Grand Besançon Métropole proposent concrètement en matière de numérique, et sur les portes d’entrée officielles pour s’y retrouver."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Collectivité",
          "value": "Grand Besançon Métropole, communauté urbaine de 67 communes"
        },
        {
          "label": "Démarches en ligne",
          "value": "Un portail commun « Mes démarches », quatorze catégories"
        },
        {
          "label": "Open data",
          "value": "Une centaine de jeux de données sur data.grandbesancon.fr"
        },
        {
          "label": "Fibre",
          "value": "Zone d’initiative privée sur l’essentiel du Grand Besançon, réseau public Doubs THD ailleurs"
        },
        {
          "label": "Inclusion",
          "value": "Conseillers numériques et maisons France Services"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Une communauté urbaine et une ville, un seul guichet web"
    },
    {
      "type": "p",
      "text": "Depuis le 1er juillet 2019, l’intercommunalité bisontine est une communauté urbaine et porte le nom de Grand Besançon Métropole. Elle réunit 67 communes autour de la ville-centre et constitue l’intercommunalité la plus peuplée du Doubs.[^1] La Ville de Besançon conserve ses compétences propres (état civil, écoles, espace public…), mais les deux collectivités ont opté pour une présence en ligne commune : les sites [besancon.fr](https://www.besancon.fr/) et [grandbesancon.fr](https://www.grandbesancon.fr/) reposent sur la même plateforme et le même portail de démarches.[^2]"
    },
    {
      "type": "p",
      "text": "Pour l’usager, la distinction entre « Ville » et « Métropole » importe finalement peu : le guichet numérique est le même, du renouvellement d’un passeport au calendrier de collecte des déchets."
    },
    {
      "type": "h2",
      "text": "Les démarches en ligne : ce qui existe aujourd’hui"
    },
    {
      "type": "p",
      "text": "Le portail « Mes démarches » regroupe quatorze catégories, de l’état civil à l’urbanisme en passant par l’eau et l’assainissement, les élections, le logement ou les subventions aux associations.[^1] Parmi les services mis en avant :"
    },
    {
      "type": "ul",
      "items": [
        "la demande d’actes d’état civil et les rendez-vous pour cartes d’identité et passeports ;",
        "le paiement en ligne de factures ;",
        "Besançon Proxim’cité, pour signaler un problème constaté sur l’espace public de la ville-centre (les autres communes passent par leur mairie) ;",
        "le plan interactif du territoire, qui superpose les couches cartographiques de la collectivité ;",
        "la plateforme participative « Ateliers citoyens »."
      ]
    },
    {
      "type": "p",
      "text": "Ces services ne prétendent pas à l’exhaustivité — certaines démarches restent au guichet — mais ils couvrent l’essentiel du quotidien administratif, et une catégorie entière du portail est consacrée à l’accompagnement aux démarches pour les personnes moins à l’aise en ligne.[^2]"
    },
    {
      "type": "h2",
      "text": "L’open data, une matière première pour les développeurs"
    },
    {
      "type": "p",
      "text": "C’est sans doute le volet le plus intéressant pour qui construit des applications ou des modèles. Grand Besançon Métropole, la Ville et le [CCAS de Besançon](/blog/ccas-besancon-rendez-vous-age-ia-micropolis-2024) alimentent une plateforme commune, [data.grandbesancon.fr](https://data.grandbesancon.fr/), qui propose une centaine de jeux de données répartis en une dizaine de thématiques : administration, environnement, transport et mobilité, élections, actes et délibérations, information géographique, citoyenneté et solidarité.[^3]"
    },
    {
      "type": "p",
      "text": "Le réseau de transport Ginko, organisé par Grand Besançon Métropole et exploité par Keolis, y publie ses propres données (bus, tramway) ; ses horaires au format GTFS, complétés par un flux temps réel SIRI, sont également référencés sur le point d’accès national transport.data.gouv.fr.[^4] Pour [un développeur bisontin](/blog/se-former-ia-besancon-guide-voies-acces), un prototype d’application mobilité ou de tableau de bord environnemental peut donc s’appuyer sur des données locales réutilisables, sans partenariat formel."
    },
    {
      "type": "h2",
      "text": "Fibre et réseaux : qui fait quoi"
    },
    {
      "type": "p",
      "text": "Le déploiement de la fibre optique dans le Doubs repose sur deux logiques distinctes. Sur l’essentiel du Grand Besançon (l’ancien périmètre de l’agglomération), on est en zone dite « AMII » : un opérateur privé, Orange en l’occurrence, déploie le réseau sur ses fonds propres, dans le cadre d’une convention avec l’État et les collectivités. Ailleurs, dans les communes moins denses du département, c’est le Syndicat mixte Doubs Très Haut Débit, créé en 2013 à l’initiative du Département, qui porte le réseau d’initiative publique avec l’objectif de desservir l’ensemble des foyers et entreprises de sa zone.[^5]"
    },
    {
      "type": "p",
      "text": "Pour savoir quels opérateurs et quels débits sont disponibles à une adresse donnée — un logement comme un futur local professionnel —, l’outil de référence reste « Ma connexion internet », publié par l’[Arcep](https://www.arcep.fr/), le régulateur des télécoms.[^6]"
    },
    {
      "type": "h2",
      "text": "Inclusion numérique : l’accompagnement humain"
    },
    {
      "type": "p",
      "text": "Dématérialiser ne suffit pas ; encore faut-il que chacun puisse suivre. Deux dispositifs nationaux structurent cet accompagnement sur le territoire. Le programme Conseiller numérique, lancé en 2021 et porté par l’Agence nationale de la cohésion des territoires (en cours de transformation en label en 2026), finance des médiateurs professionnels qui aident les particuliers à prendre en main leurs outils et à réaliser leurs démarches ; une cartographie nationale des lieux d’inclusion numérique permet de trouver la structure la plus proche.[^7] Les maisons France Services proposent, elles, un accompagnement gratuit et sans rendez-vous pour les démarches des principaux organismes publics (impôts, retraite, santé, France Travail…).[^8]"
    },
    {
      "type": "p",
      "text": "À Besançon, ces dispositifs viennent compléter les relais de proximité que sont le CCAS, les bibliothèques ou les structures de quartier, et le portail « Mes démarches » renvoie explicitement vers l’accompagnement aux démarches administratives.[^2]"
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour l’IA",
      "text": "Les modèles et les agents ont besoin de données locales fiables, structurées et librement réutilisables. Plateforme open data de la collectivité, horaires de transport en GTFS, délibérations en formats ouverts : autant de briques concrètes pour des outils utiles aux habitants — assistant de démarches, prévision d’affluence, analyse de documents publics. Avant de lancer un projet, vérifier la licence de chaque jeu de données reste indispensable."
    },
    {
      "type": "h2",
      "text": "Où chercher l’information officielle"
    },
    {
      "type": "p",
      "text": "Le paysage numérique bisontin n’a rien de spectaculaire, et c’est peut-être sa qualité : les briques sont en place, documentées et accessibles depuis quelques portes d’entrée stables — les deux sites de la collectivité pour les démarches, data.grandbesancon.fr et les portails nationaux pour les données, le syndicat Doubs Très Haut Débit et l’outil de l’Arcep pour la fibre, la cartographie des conseillers numériques pour l’accompagnement."
    },
    {
      "type": "p",
      "text": "Les prochaines étapes — enrichissement du catalogue open data, usages de l’intelligence artificielle dans les services publics locaux — restent à observer. Nous y reviendrons sur ce blog à mesure que des éléments vérifiables apparaîtront."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Grand Besançon Métropole et Ville de Besançon — site officiel",
      "publisher": "Grand Besançon Métropole",
      "url": "https://www.grandbesancon.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 2,
      "title": "Ville de Besançon et Grand Besançon Métropole — site officiel",
      "publisher": "Ville de Besançon",
      "url": "https://www.besancon.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 3,
      "title": "OpenData Besançon — plateforme de données ouvertes",
      "publisher": "Grand Besançon Métropole, Ville de Besançon, CCAS de Besançon",
      "url": "https://data.grandbesancon.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 4,
      "title": "Réseau urbain Ginko — jeux de données GTFS et SIRI sur le point d’accès national",
      "publisher": "transport.data.gouv.fr",
      "url": "https://transport.data.gouv.fr/datasets?q=ginko",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 5,
      "title": "Syndicat Mixte Doubs Très Haut Débit",
      "publisher": "Syndicat mixte Doubs Très Haut Débit",
      "url": "https://www.doubs-thd.org/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 6,
      "title": "Arcep — Autorité de régulation des communications électroniques, des postes et de la distribution de la presse",
      "publisher": "Arcep",
      "url": "https://www.arcep.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 7,
      "title": "Conseiller numérique — Accompagnement et autonomie numérique",
      "publisher": "ANCT",
      "url": "https://conseiller-numerique.gouv.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 8,
      "title": "France services — site officiel",
      "publisher": "France services (ANCT)",
      "url": "https://www.france-services.gouv.fr/",
      "type": "official",
      "date": "2026-09-06"
    }
  ]
};
